import { useMouseMove } from "@asup/context-menu";
import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { classMerge } from "../utility/classMerge";
import { useScaffold } from "./Scaffold";
import styles from "./Scaffold.module.css";
import { IScaffoldAction, PREVIEW_ROW_HEIGHT_CHANGE } from "./context/interface";
import { MIN_SCAFFOLD_ROW_HEIGHT_PX } from "./interface";

export interface ScaffoldRowResizeHandleProps {
  /** Row id being potentially resized. */
  rowId: string;
}

/**
 * Resize handle rendered at the left edge of a scaffold row.
 * Dragging the handle stores transient preview height and commits a single row-height
 * update via scaffold context callbacks on mouse release.
 *
 * @param props - Component props.
 * @param props.rowId - Row id being potentially resized.
 * @returns A resize handle div, rendered as disabled when resize interactions are unavailable.
 */
export const ScaffoldRowResizeHandle = ({ rowId }: ScaffoldRowResizeHandleProps) => {
  const scaffoldContext = useScaffold();
  const leavingRow = scaffoldContext.state.leavingRows.find((r) => r.id === rowId);
  const row = scaffoldContext.settings.rows.find((r) => r.id === rowId) ?? leavingRow;
  const canResize =
    scaffoldContext.settings.disabled !== true &&
    scaffoldContext.settings.onChangeRow !== undefined &&
    !leavingRow &&
    row !== undefined &&
    !scaffoldContext.state.enteringRowIds.has(rowId);

  const resizeSessionRef = useRef<{
    startY: number;
    startHeight: number;
    latestHeight: number;
  } | null>(null);
  const rowRef = useRef(row);
  const leavingRowRef = useRef(leavingRow);
  const onChangeRowRef = useRef(scaffoldContext.settings.onChangeRow);
  const dispatchRef = useRef<React.Dispatch<IScaffoldAction<unknown>>>(scaffoldContext.dispatch);

  /**
   * Keeps mutable refs synchronised with the latest row data for document-level mouse handlers.
   */
  useLayoutEffect(() => {
    rowRef.current = row;
    leavingRowRef.current = leavingRow;
    onChangeRowRef.current = scaffoldContext.settings.onChangeRow;
    dispatchRef.current = scaffoldContext.dispatch;
  }, [row, leavingRow, scaffoldContext.settings.onChangeRow, scaffoldContext.dispatch]);

  /**
   * Applies transient row height updates while the user drags the resize handle.
   *
   * @param e - Native mouse move event from useMouseMove.
   */
  const handleResizeMouseMove = (e: React.MouseEvent<Element, MouseEvent>) => {
    const session = resizeSessionRef.current;
    /* istanbul ignore next -- listeners only attached during active resize */
    if (session === null) {
      return;
    }

    const nextHeight = Math.max(
      MIN_SCAFFOLD_ROW_HEIGHT_PX,
      Math.round(session.startHeight + (e.clientY - session.startY)),
    );

    if (nextHeight === session.latestHeight) {
      return;
    }

    session.latestHeight = nextHeight;
    dispatchRef.current({ operation: PREVIEW_ROW_HEIGHT_CHANGE, rowId, height: nextHeight });
  };

  /**
   * Completes an active row resize, resets transient state, and emits a single row update.
   */
  const handleResizeMouseUp = useCallback(() => {
    const session = resizeSessionRef.current;
    /* istanbul ignore next -- listeners only attached during active resize */
    if (session === null) {
      return;
    }

    const latestHeight = session.latestHeight;
    resizeSessionRef.current = null;
    dispatchRef.current({ operation: PREVIEW_ROW_HEIGHT_CHANGE, rowId, height: null });

    const currentRow = rowRef.current;
    const currentLeavingRow = leavingRowRef.current;
    if (currentRow !== undefined && !currentLeavingRow && latestHeight !== currentRow.height) {
      onChangeRowRef.current?.(rowId, { ...currentRow, height: latestHeight });
    }
  }, [rowId]);

  /**
   * Starts a row height drag operation from this resize handle.
   *
   * @param event - React mouse down event from the resize handle.
   */
  const handleResizeMouseDown = (event: React.MouseEvent<HTMLElement | SVGElement>) => {
    if (canResize) {
      event.preventDefault();

      const initialHeight = Math.max(MIN_SCAFFOLD_ROW_HEIGHT_PX, rowRef.current!.height);
      resizeSessionRef.current = {
        startY: event.clientY,
        startHeight: initialHeight,
        latestHeight: initialHeight,
      };
      dispatchRef.current({ operation: PREVIEW_ROW_HEIGHT_CHANGE, rowId, height: initialHeight });
    }
  };

  /**
   * Clears transient preview state if the component unmounts mid-drag.
   */
  useEffect(() => {
    return () => {
      resizeSessionRef.current = null;
      dispatchRef.current({ operation: PREVIEW_ROW_HEIGHT_CHANGE, rowId, height: null });
    };
  }, [rowId]);

  const { onMouseDown } = useMouseMove({
    onMouseDown: handleResizeMouseDown,
    onMouseMove: handleResizeMouseMove,
    onMouseUp: handleResizeMouseUp,
  });

  return (
    <div
      className={classMerge([
        styles.rowResizeHandle,
        canResize ? undefined : styles.rowResizeHandleDisabled,
      ])}
      onMouseDown={onMouseDown}
    />
  );
};

ScaffoldRowResizeHandle.displayName = "ScaffoldRowResizeHandle";
